/**
 * useBranding — wendet das White-Label-Branding eines Kunden an, indem es die
 * Panel-Design-Tokens als CSS Custom Properties am :root überschreibt
 * (`document.documentElement.style.setProperty`). KEIN CSS-Datei-Rewrite —
 * der dokumentierte Profi-Standard 2026 (Backdrop/Drupal Canvas Styles).
 *
 * Inline-Styles am :root gewinnen gegenüber den `@theme`-Werten aus main.css,
 * daher reicht setProperty. clearBranding() entfernt sie wieder (z.B. wenn der
 * Kiosk verlassen wird), sodass der Standard-miraCMS-Look zurückkommt.
 */
import type { ProjectBranding } from '@/types/api'
import { setBrandName } from '@/lib/document-title'

/** Tokens, die das Branding überschreiben darf (siehe assets/css/main.css). */
const OVERRIDABLE = [
  '--color-primary',
  '--color-primary-dark',
  '--color-primary-light',
  '--color-bg',
  '--color-fg',
] as const

/** #rgb/#rrggbb → {r,g,b}. Gibt null bei ungültigem Wert (defensiv). */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(hex)
  if (!m?.[1]) return null
  let h: string = m[1]
  if (h.length === 3) h = h.split('').map((c) => c + c).join('')
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  }
}

/** Dunklere Variante (für --color-primary-dark, Hover/aktive Buttons). */
function darken(hex: string, amount = 0.15): string {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex
  const f = (c: number) => Math.max(0, Math.round(c * (1 - amount)))
  const h = (c: number) => f(c).toString(16).padStart(2, '0')
  return `#${h(rgb.r)}${h(rgb.g)}${h(rgb.b)}`
}

/** Transparente Variante (für --color-primary-light, Soft-Backgrounds). */
function rgba(hex: string, alpha: number): string {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`
}

/** Branding anwenden. `null`/leer → alle Overrides entfernen (Reset). */
export function applyBranding(branding: ProjectBranding | null | undefined): void {
  const root = document.documentElement
  // Immer erst zurücksetzen, damit ein Wechsel sauber ist.
  for (const v of OVERRIDABLE) root.style.removeProperty(v)
  // Tab-Titel-Suffix: Kundenname wenn gebrandet, sonst Standard-Produktname.
  setBrandName(branding?.app_name ?? null)
  if (!branding) return

  if (branding.color_primary) {
    root.style.setProperty('--color-primary', branding.color_primary)
    root.style.setProperty('--color-primary-dark', darken(branding.color_primary))
    root.style.setProperty('--color-primary-light', rgba(branding.color_primary, 0.12))
  }
  if (branding.color_bg) root.style.setProperty('--color-bg', branding.color_bg)
  if (branding.color_fg) root.style.setProperty('--color-fg', branding.color_fg)

  setFavicon(branding.favicon ?? null)
}

export function clearBranding(): void {
  applyBranding(null)
}

// Ursprüngliches Favicon merken (einmalig), um es beim Verlassen wiederherzustellen.
let defaultFaviconHref: string | null = null

/**
 * Browser-Tab-Favicon je Kunde setzen. Wir zeigen auf eine ANDERE URL pro Kunde
 * (eigene Datei auf cdn.mirateq.com) — daher kein Cache-Konflikt: jede URL wird
 * unabhängig vom Browser/Cloudflare gecacht. `null` stellt das Standard-Favicon
 * wieder her.
 */
function setFavicon(href: string | null): void {
  let link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]')
  if (defaultFaviconHref === null) {
    defaultFaviconHref = link?.getAttribute('href') ?? ''
  }
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  link.href = href ?? defaultFaviconHref ?? ''
}
